<template>
  <div class="px-5 sm:px-6 lg:px-10 py-6">
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-[22px] sm:text-[24px] leading-7 font-bold text-gray-900">
        Banks
      </h1>
    </div>

    <div v-if="!can.view" class="mt-10"></div>

    <template v-else>
      <DynamicTable
        wrapperClass="banks-table"
        :showHeader="false"
        :rows="banks"
        :columns="columns"
        :rowsPerPage="1000"
        :rowsPerPageOptions="[10,20,50]"
        :showAddButton="false"
        :loading="loading"
      >
        <template #action="{ row }">
          <div class="flex items-center justify-center">
            <button
              @click="openViewModal(row)"
              class="inline-flex items-center gap-1.5 text-xs text-black hover:opacity-90"
              title="View"
            >
              <svg viewBox="0 0 24 24" class="w-4 h-4">
                <path d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7Zm0 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8Z" fill="currentColor"/>
              </svg>
              <span>View</span>
            </button>
          </div>
        </template>

        <template #payment_for="{ value }">
          <span class="truncate block" :title="value">
            {{ value }}
          </span>
        </template>
      </DynamicTable>

      <!-- Modal -->
      <div v-if="showModal" class="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4">
        <div class="modal-card relative z-[10000] bg-white rounded-2xl w-full max-w-[540px] max-h-[87vh] overflow-hidden flex flex-col">

          <button
            class="absolute top-3 right-3 w-8 h-8 rounded-full grid place-items-center hover:bg-gray-100"
            @click="closeModal"
          >
            ✕
          </button>

          <div class="px-8 pt-7 pb-2 shrink-0">
            <h2 class="text-center text-[24px] font-bold text-gray-900">
              View Bank
            </h2>
          </div>

          <div class="px-8 overflow-y-auto pb-4">
            <DynamicForm
              :key="formKey"
              :title="''"
              :schema="formSchema"
              v-model="formModel"
              :showActions="false"
              variant="transparent"
              wrapperClass="bg-transparent p-0"
              :disabled="true"
            >
              <template #field:pf_heading>
                <div>
                  <label class="block text-sm font-bold text-gray-800 mb-2">
                    Payment For
                  </label>
                  <div class="grid grid-cols-3 gap-x-6 gap-y-4 mt-1">
                    <label class="flex items-center gap-2">
                      <input type="checkbox" v-model="formModel.pf_contracts" disabled />
                      <span>Contracts</span>
                    </label>
                    <label class="flex items-center gap-2">
                      <input type="checkbox" v-model="formModel.pf_booking" disabled />
                      <span>Booking</span>
                    </label>
                  </div>
                </div>
              </template>
            </DynamicForm>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import api from "@/api";
import DynamicTable from "@/components/DynamicTable.vue";
import DynamicForm from "@/components/DynamicForm.vue";
import { loadMyPermissions, hasAny } from "@/utils/permissions";

export default {
  name: "Banks",
  components: { DynamicTable, DynamicForm },

  data() {
    return {
      can: {
        view: false, // payments.banks.view
      },

      columns: [
        { key: "id",          label: "ID",          width: "250px" },
        { key: "name",        label: "Bank Name",   width: "250px" },
        { key: "company_name",  label: "Company Name",  width: "250px" },
        { key: "payment_for", label: "Payment For", width: "250px" },
        { key: "action",      label: "Action",      width: "250px" },
      ],

      banks:   [],
      loading: false,
      showModal: false,
      formKey:   0,
      formModel: {
        name:         "",
        company_id:   "",
        pf_contracts: false,
        pf_booking:   false,
      },

      companies: [],

      formFields: [
        { key: "name",       label: "Bank",    type: "text"   },
        { key: "company_id", label: "Company", type: "select", options: [] },
        { key: "pf_heading", slot: true },
      ],
    };
  },

  computed: {
    formSchema() {
      return {
        sections: [{ fields: this.formFields }],
      };
    },
  },

  async created() {
    await this.ensurePermissions();
    if (!this.can.view) return;

    this.loadBanks();
    this.loadCompanies();
  },

  methods: {
    async ensurePermissions() {
      try {
        await loadMyPermissions(false);
        this.can.view = hasAny(["payments.banks.view"]);
      } catch (_) {
        this.can.view = false;
      }
    },

    normalizeBank(raw) {
      let paymentFor = [];

      if (Array.isArray(raw.payment_for)) {
        paymentFor = raw.payment_for;
      } else if (typeof raw.payment_for === "string" && raw.payment_for) {
        if (raw.payment_for.trim().startsWith("[")) {
          try {
            const parsed = JSON.parse(raw.payment_for);
            paymentFor = Array.isArray(parsed) ? parsed : [raw.payment_for];
          } catch {
            paymentFor = [raw.payment_for];
          }
        } else {
          paymentFor = raw.payment_for.split(",").map((s) => s.trim());
        }
      }

      return {
        id:          raw.id,
        name:        raw.name        || "",
        company_id:  raw.company_id  || "",
        company_name: raw.company_name || "",
        payment_for: paymentFor.join(", "),
      };
    },

    async loadBanks() {
      this.loading = true;
      try {
        const res  = await api.get("/accounting/banks");
        const data = res?.data?.data ?? res?.data ?? [];
        this.banks = Array.isArray(data)
          ? data.map(this.normalizeBank).sort((a, b) => a.id - b.id)
          : [];
      } catch (err) {
        console.error("Failed to load banks:", err);
        this.banks = [];
      } finally {
        this.loading = false;
      }
    },

    async loadCompanies() {
      try {
        const res      = await api.get("/accounting/companies");
        const data     = res?.data?.data ?? res?.data ?? [];
        this.companies = Array.isArray(data) ? data : [];
      } catch (err) {
        console.error("Failed to load companies:", err);
        this.companies = [];
      }

      const field = this.formFields.find((f) => f.key === "company_id");
      if (field) {
        field.options = this.companies.map((c) => ({
          value: c.id,
          label: c.name,
        }));
      }
    },

    openViewModal(row) {
      const paymentForStr = row.payment_for || "";
      this.formModel = {
        name:         row.name       || "",
        company_id:   row.company_id || "",
        pf_contracts: paymentForStr.includes("Contracts"),
        pf_booking:   paymentForStr.includes("Booking"),
      };
      this.formKey++;
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
    },
  },
};
</script>

<style scoped>
:deep(.modal-card input[type="checkbox"]) {
  appearance: none;
  width: 18px;
  height: 18px;
  border: 1.8px solid #000;
  border-radius: 3px;
  background: #fff;
  position: relative;
}

:deep(.modal-card input[type="checkbox"]:checked::after) {
  content: "";
  position: absolute;
  left: 4px;
  top: 0px;
  width: 6px;
  height: 10px;
  border-right: 2px solid #000;
  border-bottom: 2px solid #000;
  transform: rotate(45deg);
}
</style>
